import axios from 'axios'
import { proxiedGet, proxiedPost, directGet, memberCalls2Get } from '@/core/http'
import {
    getMemberCalls2Url,
    getHydraUrl,
    getCreateUuidUrl,
    getCreateAccountUrl,
    getCheckAllowReferrerUrl,
    getCreditCardCheckValidateUrl
} from '@/core/http/http-helper'
import type {
    TOmanRegion,
    TOmanCountryItem,
    TOmanCreateAccountBody,
    TOmanSaveCardAliasPayload,
    TOmanCreditcardAlias,
    PaymentsV3CreateRequest,
    PaymentsV3CreateResponse,
    PaymentsV3StatusResponse,
    PaymentsV3DuplicateOrderResponse,
    TOmanShippingContact
} from '../types/oman.types'
import type { TUuidPostBody, TReferralValidation, TAddressHistoryResponse } from '@/core/checkout/checkout.types'
import { appStore } from '@/core/store/jotai.store'
import { hotFormatRawAtom } from '@/core/store/hotFormat.store'
import type { HotFormatResponse } from '@/core/types/hotFormat.type'

const ALPHA3 = 'OMN'
const ALPHA2 = 'OM'
const X_APPLICATION = 'asia.fg.om'

const HYDRA_HEADERS = (token: string) => ({
    authorization: `Bearer ${token}`,
    'x-application': X_APPLICATION
})

const AUTH_HYDRA_HEADER = (token: string) => ({
    'Authorization-hydra': `Bearer ${token}`
})

// ─── URL builders ─────────────────────────────────────────────────────────────

const getFormatDetailUrl = (uuid: string) => getMemberCalls2Url(`hot/format/${ALPHA3}/feelGreat/detail-v2/${uuid}?ordercalC=1&expandGenBeta=1`)

const getFormatUpdateUrl = (skipValidate = false) => getMemberCalls2Url(`hot/formatV2/${ALPHA3}/feelGreat/address${skipValidate ? '?isSkipValidate=1' : ''}`)

// ─── Geo data ─────────────────────────────────────────────────────────────────

interface CountryListApiResponse {
    success: boolean
    data: TOmanCountryItem[]
}

export interface OmanCreditCardValidationResponse {
    allowAccess: boolean
    isAllowedIssuingNetwork: boolean
}

export const validateOmanCreditCard = async (baId: string, creditCard: string): Promise<OmanCreditCardValidationResponse> =>
    proxiedPost<OmanCreditCardValidationResponse>(getCreditCardCheckValidateUrl(), {
        country_code: ALPHA3,
        platform: 'feelGreat',
        ba_id: baId,
        credit_card: creditCard
    })

export const fetchOmanCountryList = async (): Promise<TOmanCountryItem[]> => {
    const resp = await memberCalls2Get<CountryListApiResponse>('hot/remoteStorage/ddb/data/country_omn')
    return resp.data ?? []
}

interface HydraRegionItem {
    name: string
    code: string
    value: string
}

export const fetchOmanRegions = async (countryCode: string = ALPHA2): Promise<TOmanRegion[]> => {
    const url = getHydraUrl(`v5a/countries/${countryCode}/regions.json`)
    const data = await directGet<{ items?: HydraRegionItem[] } | HydraRegionItem[]>(url)
    const raw = Array.isArray(data) ? data : ((data as { items?: HydraRegionItem[] }).items ?? [])
    return raw.map(r => ({ id: r.code, name: r.name, code: r.code, value: r.value }))
}

// ─── Checkout init ────────────────────────────────────────────────────────────

interface TopupPeriodResponse {
    success: boolean
    entryPeriod: string
    country: string
    isTopup: boolean
    isForceTopup: boolean
}

export const fetchOmanEntryPeriod = async (): Promise<string> => {
    const data = await proxiedPost<TopupPeriodResponse>(getMemberCalls2Url('topup/period'), { country: ALPHA2, system: 'AO', joinPeriod: '' })
    return data.entryPeriod
}

export const validateOmanReferrer = async (baId: string): Promise<TReferralValidation> =>
    proxiedGet<TReferralValidation>(getCheckAllowReferrerUrl(ALPHA3, baId))

export const createOmanUuid = async (body: TUuidPostBody, isOrderCalc?: boolean): Promise<{ uuid: string }> =>
    proxiedPost<{ uuid: string }>(getCreateUuidUrl(ALPHA3, isOrderCalc), body)

// ─── Format API ───────────────────────────────────────────────────────────────

export const getOmanFormat = async (uuid: string): Promise<Record<string, unknown>> => {
    const data = await proxiedGet<Record<string, unknown>>(getFormatDetailUrl(uuid))
    appStore.set(hotFormatRawAtom, data as unknown as HotFormatResponse)
    return data
}

export const putOmanFormat = async (payload: Record<string, unknown>, token?: string): Promise<Record<string, unknown>> => {
    const url = getFormatUpdateUrl()
    const headers = token ? AUTH_HYDRA_HEADER(token) : {}
    const { data } = await axios.put<Record<string, unknown>>(url, payload, { headers })
    return data
}

export const updateOmanCartQty = async (uuid: string, shipmentPacks: string): Promise<Record<string, unknown>> => {
    const url = getFormatUpdateUrl(true)
    const { data } = await axios.put<Record<string, unknown>>(url, {
        uuid,
        shipment_packs: shipmentPacks
    })
    return data
}

// ─── Account creation ─────────────────────────────────────────────────────────

export const createOmanAccount = async (body: TOmanCreateAccountBody): Promise<Record<string, unknown>> =>
    proxiedPost<Record<string, unknown>>(getCreateAccountUrl(ALPHA3), body)

// ─── Address history ──────────────────────────────────────────────────────────

export const fetchOmanAddressHistory = async (baId: string, token: string): Promise<TAddressHistoryResponse> => {
    const url = getMemberCalls2Url(`etlV2/addressHistory?baId=${baId}&ushopCountryCode=${ALPHA3}`)
    const { data } = await axios.get<TAddressHistoryResponse>(url, {
        headers: AUTH_HYDRA_HEADER(token)
    })
    return data
}

// ─── Member shipping address PUT ──────────────────────────────────────────────

interface TOmanMemberShippingBody {
    uuid: string
    fullname: string
    address: string
    address2?: string
    city: string
    state: string
    country: string
    phone?: string
    email?: string
    shipment_options: string
    token: string
    type: string
    ba_status: string
    login_id: string
    login_name: string
    login_name_native: string
    shipment_packs: string
}

export const putOmanMemberShipping = async (body: TOmanMemberShippingBody): Promise<Record<string, unknown>> => {
    const { data } = await axios.put(getFormatUpdateUrl(), body)
    return data
}

// ─── Shipping method update ───────────────────────────────────────────────────

export const updateOmanShippingMethod = async (
    uuid: string,
    shipmentOptions: string,
    skipValidate = false,
    contact?: TOmanShippingContact
): Promise<Record<string, unknown>> => {
    const { data } = await axios.put<Record<string, unknown>>(getFormatUpdateUrl(skipValidate), {
        uuid,
        shipment_options: shipmentOptions,
        ...contact
    })
    return data
}

export const syncOmanShippingMethodFormat = async (
    uuid: string,
    shipmentOptions: string,
    skipValidate = false,
    contact?: TOmanShippingContact
): Promise<Record<string, unknown>> => {
    const putResult = await updateOmanShippingMethod(uuid, shipmentOptions, skipValidate, contact)
    if (putResult?.success === false) return putResult
    return getOmanFormat(uuid)
}

// ─── Pre-payment checks ──────────────────────────────────────────────────────

export const checkDuplicateOrderV3 = async (uuid: string): Promise<PaymentsV3DuplicateOrderResponse> => {
    const url = getMemberCalls2Url(`hot/payments/v3/duplicateOrder/${ALPHA3}/feelGreat/${uuid}`)
    const { data } = await axios.get<PaymentsV3DuplicateOrderResponse>(url)
    return data
}

// ─── Saved credit cards ───────────────────────────────────────────────────────

export const fetchOmanSavedCards = async (_baId: string, token: string, countryCode2: string = ALPHA2): Promise<{ items: TOmanCreditcardAlias[] }> => {
    const url = getHydraUrl(`v5a/customers/me/creditcardaliases/${countryCode2}`)
    const { data } = await axios.get(url, { headers: HYDRA_HEADERS(token) })
    const items: TOmanCreditcardAlias[] = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : []
    return { items }
}

export const deleteOmanSavedCard = async (cardAlias: string, token: string, countryCode2: string = ALPHA2): Promise<void> => {
    const url = getHydraUrl(`v5a/customers/me/creditcardaliases/${countryCode2}/${cardAlias}`)
    await axios.delete(url, { headers: HYDRA_HEADERS(token) })
}

// ─── Payment flow APIs ────────────────────────────────────────────────────────

export const saveCardAlias = async (payload: TOmanSaveCardAliasPayload, token: string, countryCode2: string = ALPHA2): Promise<{ href: string }> => {
    const url = getHydraUrl(`v5a/customers/me/creditcardaliases/${countryCode2}`)
    const { data } = await axios.post(url, payload, { headers: HYDRA_HEADERS(token) })
    return data
}

export const checkCardAlias = async (href: string, token: string): Promise<{ data: Record<string, unknown>; headers: Record<string, string> }> => {
    // The downstream `formatV2/.../address` PUT needs to echo Hydra's response
    // headers verbatim, so capture them alongside the body. Axios exposes
    // headers as `AxiosResponseHeaders` (an object-like wrapper); coerce to a
    // plain `Record<string,string>` for predictable JSON.stringify.
    const res = await axios.get(href, { headers: HYDRA_HEADERS(token) })
    const headers: Record<string, string> = {}
    for (const [key, value] of Object.entries(res.headers ?? {})) {
        if (typeof value === 'string') headers[key] = value
        else if (Array.isArray(value)) headers[key] = value.join(',')
    }
    return { data: res.data as Record<string, unknown>, headers }
}

export const updateUuidWithAlias = async (
    uuid: string,
    aliasDetailCurl: string,
    aliasDetailHeader: string,
    aliasDetailBody: string,
    token: string
): Promise<Record<string, unknown>> => {
    const url = getFormatUpdateUrl(true)
    const { data } = await axios.put(
        url,
        {
            uuid,
            hydra_alias_detail_curl: aliasDetailCurl,
            hydra_alias_detail_response_header: aliasDetailHeader,
            hydra_alias_detail_response_body: aliasDetailBody
        },
        { headers: AUTH_HYDRA_HEADER(token) }
    )
    return data
}

/** PUT billing address / alias info before createPaymentV3 */
export const processFormatBeforePayment = async (
    uuid: string,
    options: {
        billingSameAsShipping: boolean
        billingAddress?: {
            fullname?: string
            address1?: string
            address2?: string
            city?: string
            state?: string
            zip?: string
        }
        customerCountry?: string
        aliasName?: string
        aliasData?: {
            curl: string
            header: string
            response: string
        }
    }
): Promise<Record<string, unknown>> => {
    const url = getFormatUpdateUrl(true)
    const body: Record<string, unknown> = {
        uuid,
        ship_address_options: !options.billingSameAsShipping
    }

    if (!options.billingSameAsShipping && options.billingAddress) {
        body.ship_address_fullname = options.billingAddress.fullname ?? ''
        body.customer_main_address = options.customerCountry ?? ALPHA2
        body.ship_address_address1 = options.billingAddress.address1 ?? ''
        body.ship_address_address2 = options.billingAddress.address2 ?? ''
        body.ship_address_city = options.billingAddress.city ?? ''
        body.ship_address_state = options.billingAddress.state ?? ''
        body.ship_address_zip = options.billingAddress.zip ?? ''
    }

    if (options.aliasName) {
        body.alias_name = options.aliasName
    }
    if (options.aliasData) {
        body.hydra_alias_curl = options.aliasData.curl
        body.hydra_alias_header = options.aliasData.header
        body.hydra_alias_response = options.aliasData.response
    }

    const { data } = await axios.put(url, body)
    return data
}

// ─── PaymentsV3 APIs ──────────────────────────────────────────────────────────

export const createPaymentV3 = async (body: PaymentsV3CreateRequest, token: string): Promise<PaymentsV3CreateResponse> => {
    const url = getMemberCalls2Url(`hot/payments/v3/${ALPHA3}/feelGreat/variable`)
    const { data } = await axios.post<PaymentsV3CreateResponse>(url, body, {
        headers: { ...AUTH_HYDRA_HEADER(token), 'Content-Type': 'application/json' }
    })
    return data
}

export const pollPaymentStatusV3 = async (gatewayRefId: string): Promise<PaymentsV3StatusResponse> => {
    const url = getMemberCalls2Url(`hot/payments/v3/status/${gatewayRefId}`)
    const { data } = await axios.get<PaymentsV3StatusResponse>(url)
    return data
}

export const saveHydraLogV3 = async (body: {
    gateway_ref_id: string
    order_number?: string
    hydra_curl?: string
    hydra_response_header?: string
    hydra_response_body: string
    hydra_status: 'success' | 'fail' | 'timeout' | 'waiting'
}): Promise<void> => {
    const url = getMemberCalls2Url('hot/payments/v3/createOrderViaFE/hydra-log')
    await axios.post(url, body)
}

export const completeOrderV3 = async (body: { gateway_ref_id: string; order_number?: string; payment_status: 'success' | 'fail' }): Promise<void> => {
    const url = getMemberCalls2Url('hot/payments/v3/createOrderViaFE/complete')
    await axios.post(url, body)
}

const buildGatewayFormFields = (gatewayData: Record<string, string | undefined>) => {
    const fields = { ...gatewayData }
    const encrypted = fields.encrypted
    const accessCode = fields.accessCode

    delete fields.payment_action_url
    delete fields.encrypted
    delete fields.accessCode

    if (encrypted) fields.encRequest = encrypted
    if (accessCode) fields.access_code = accessCode

    return fields
}

/** Auto-submit hidden form for gateway_redirect (Bank Muscat / Cybersource SSL) */
export function submitGatewayForm(gatewayData: Record<string, string | undefined>) {
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = gatewayData.payment_action_url ?? ''

    for (const [key, value] of Object.entries(buildGatewayFormFields(gatewayData))) {
        if (value === undefined) continue
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = key
        input.value = String(value)
        form.appendChild(input)
    }

    document.body.appendChild(form)
    form.submit()
}
